import type { UtilityRouteDefinition } from '@saas-pages/shared/public/registry'
import type { UnitCategoryKey, UnitKey } from '@/shared/unit-converter'

export type UnitConverterPairKey =
  | 'sm-v-m' | 'm-v-sm' | 'sm-v-mm' | 'mm-v-sm'
  | 'dyujmy-v-sm' | 'sm-v-dyujmy' | 'futy-v-metry' | 'metry-v-futy'
  | 'kg-v-grammy' | 'grammy-v-kg' | 'tonny-v-kg' | 'kg-v-tonny'
  | 'funty-v-kg' | 'kg-v-funty'
  | 'celsij-v-farengejt' | 'farengejt-v-celsij'
  | 'km-ch-v-m-s' | 'm-s-v-km-ch'

export type UnitConverterRouteKey = 'unit-converter' | `unit-converter-${UnitConverterPairKey}`

export interface UnitConverterPair {
  pairKey: UnitConverterPairKey
  path: `/${UnitConverterPairKey}`
  category: UnitCategoryKey
  fromUnit: UnitKey
  toUnit: UnitKey
  title: string
  shortTitle: string
  formula: string
  example: string
  reversePairKey: UnitConverterPairKey
}

export const unitConverterPairs: readonly UnitConverterPair[] = [
  { pairKey: 'sm-v-m', path: '/sm-v-m', category: 'length', fromUnit: 'cm', toUnit: 'm', title: 'Сантиметры в метры', shortTitle: 'см в м', formula: 'метры = сантиметры ÷ 100', example: '250 см = 2,5 м', reversePairKey: 'm-v-sm' },
  { pairKey: 'm-v-sm', path: '/m-v-sm', category: 'length', fromUnit: 'm', toUnit: 'cm', title: 'Метры в сантиметры', shortTitle: 'м в см', formula: 'сантиметры = метры × 100', example: '2,5 м = 250 см', reversePairKey: 'sm-v-m' },
  { pairKey: 'sm-v-mm', path: '/sm-v-mm', category: 'length', fromUnit: 'cm', toUnit: 'mm', title: 'Сантиметры в миллиметры', shortTitle: 'см в мм', formula: 'миллиметры = сантиметры × 10', example: '12,5 см = 125 мм', reversePairKey: 'mm-v-sm' },
  { pairKey: 'mm-v-sm', path: '/mm-v-sm', category: 'length', fromUnit: 'mm', toUnit: 'cm', title: 'Миллиметры в сантиметры', shortTitle: 'мм в см', formula: 'сантиметры = миллиметры ÷ 10', example: '125 мм = 12,5 см', reversePairKey: 'sm-v-mm' },
  { pairKey: 'dyujmy-v-sm', path: '/dyujmy-v-sm', category: 'length', fromUnit: 'in', toUnit: 'cm', title: 'Дюймы в сантиметры', shortTitle: 'дюймы в см', formula: 'сантиметры = дюймы × 2,54', example: '10 дюймов = 25,4 см', reversePairKey: 'sm-v-dyujmy' },
  { pairKey: 'sm-v-dyujmy', path: '/sm-v-dyujmy', category: 'length', fromUnit: 'cm', toUnit: 'in', title: 'Сантиметры в дюймы', shortTitle: 'см в дюймы', formula: 'дюймы = сантиметры ÷ 2,54', example: '25,4 см = 10 дюймов', reversePairKey: 'dyujmy-v-sm' },
  { pairKey: 'futy-v-metry', path: '/futy-v-metry', category: 'length', fromUnit: 'ft', toUnit: 'm', title: 'Футы в метры', shortTitle: 'футы в метры', formula: 'метры = футы × 0,3048', example: '10 футов = 3,048 м', reversePairKey: 'metry-v-futy' },
  { pairKey: 'metry-v-futy', path: '/metry-v-futy', category: 'length', fromUnit: 'm', toUnit: 'ft', title: 'Метры в футы', shortTitle: 'метры в футы', formula: 'футы = метры ÷ 0,3048', example: '3,048 м = 10 футов', reversePairKey: 'futy-v-metry' },
  { pairKey: 'kg-v-grammy', path: '/kg-v-grammy', category: 'mass', fromUnit: 'kg', toUnit: 'g', title: 'Килограммы в граммы', shortTitle: 'кг в граммы', formula: 'граммы = килограммы × 1000', example: '2,5 кг = 2500 г', reversePairKey: 'grammy-v-kg' },
  { pairKey: 'grammy-v-kg', path: '/grammy-v-kg', category: 'mass', fromUnit: 'g', toUnit: 'kg', title: 'Граммы в килограммы', shortTitle: 'граммы в кг', formula: 'килограммы = граммы ÷ 1000', example: '2500 г = 2,5 кг', reversePairKey: 'kg-v-grammy' },
  { pairKey: 'tonny-v-kg', path: '/tonny-v-kg', category: 'mass', fromUnit: 't', toUnit: 'kg', title: 'Тонны в килограммы', shortTitle: 'тонны в кг', formula: 'килограммы = тонны × 1000', example: '1,5 т = 1500 кг', reversePairKey: 'kg-v-tonny' },
  { pairKey: 'kg-v-tonny', path: '/kg-v-tonny', category: 'mass', fromUnit: 'kg', toUnit: 't', title: 'Килограммы в тонны', shortTitle: 'кг в тонны', formula: 'тонны = килограммы ÷ 1000', example: '1500 кг = 1,5 т', reversePairKey: 'tonny-v-kg' },
  { pairKey: 'funty-v-kg', path: '/funty-v-kg', category: 'mass', fromUnit: 'lb', toUnit: 'kg', title: 'Фунты в килограммы', shortTitle: 'фунты в кг', formula: 'килограммы = фунты × 0,45359237', example: '10 фунтов = 4,5359237 кг', reversePairKey: 'kg-v-funty' },
  { pairKey: 'kg-v-funty', path: '/kg-v-funty', category: 'mass', fromUnit: 'kg', toUnit: 'lb', title: 'Килограммы в фунты', shortTitle: 'кг в фунты', formula: 'фунты = килограммы ÷ 0,45359237', example: '4,5359237 кг = 10 фунтов', reversePairKey: 'funty-v-kg' },
  { pairKey: 'celsij-v-farengejt', path: '/celsij-v-farengejt', category: 'temperature', fromUnit: 'c', toUnit: 'f', title: 'Цельсий в Фаренгейт', shortTitle: '°C в °F', formula: '°F = °C × 9/5 + 32', example: '20 °C = 68 °F', reversePairKey: 'farengejt-v-celsij' },
  { pairKey: 'farengejt-v-celsij', path: '/farengejt-v-celsij', category: 'temperature', fromUnit: 'f', toUnit: 'c', title: 'Фаренгейт в Цельсий', shortTitle: '°F в °C', formula: '°C = (°F − 32) × 5/9', example: '68 °F = 20 °C', reversePairKey: 'celsij-v-farengejt' },
  { pairKey: 'km-ch-v-m-s', path: '/km-ch-v-m-s', category: 'speed', fromUnit: 'kmh', toUnit: 'ms', title: 'Километры в час в метры в секунду', shortTitle: 'км/ч в м/с', formula: 'м/с = км/ч ÷ 3,6', example: '36 км/ч = 10 м/с', reversePairKey: 'm-s-v-km-ch' },
  { pairKey: 'm-s-v-km-ch', path: '/m-s-v-km-ch', category: 'speed', fromUnit: 'ms', toUnit: 'kmh', title: 'Метры в секунду в километры в час', shortTitle: 'м/с в км/ч', formula: 'км/ч = м/с × 3,6', example: '10 м/с = 36 км/ч', reversePairKey: 'km-ch-v-m-s' },
]

const processing = {
  mode: 'local',
  label: 'Локально в браузере',
  title: 'Значения пересчитываются прямо в браузере и не отправляются на сервер',
} as const

export const unitConverterRoute = {
  routeKey: 'unit-converter',
  path: '/konverter-edinic',
  title: 'Конвертер единиц измерения',
  description: 'Переводите длину, массу, площадь, объём, температуру и скорость между бытовыми единицами. Результат обновляется локально в браузере.',
  nav: { label: 'Конвертер единиц', category: 'calculator', order: 40 },
  seo: {
    title: 'Конвертер единиц измерения онлайн - calcdeck.ru',
    description: 'Бесплатный конвертер длины, массы, площади, объёма, температуры и скорости. Мгновенный локальный расчёт без регистрации и отправки данных.',
    changefreq: 'monthly',
    priority: 1,
  },
  processing,
  analyticsKey: 'unit-converter',
  availability: 'available',
  schema: {
    applicationCategory: 'UtilitiesApplication',
    howTo: [
      'Выберите категорию единиц: длину, массу, площадь, объём, температуру или скорость.',
      'Укажите исходную и целевую единицы, затем введите значение с точкой или запятой.',
      'Проверьте автоматически обновившийся результат и скопируйте его при необходимости.',
    ],
    faq: [
      { question: 'Какие единицы доступны в конвертере?', answer: 'Доступны повседневные единицы длины, массы, площади, объёма, температуры и скорости. Валют в конвертере нет.' },
      { question: 'Как переводится температура?', answer: 'Температура пересчитывается через шкалу Цельсия с учётом смещения шкал Фаренгейта и Кельвина, а не только умножением.' },
      { question: 'Введённые значения отправляются на сервер?', answer: 'Нет. Все переводы выполняются локально в браузере.' },
    ],
  },
  relatedTools: [
    { routeKey: 'percentage-calculator', label: 'Процентный калькулятор', description: 'Процент от числа и изменение значения на процент.' },
    { routeKey: 'average-median-calculator', label: 'Среднее и медиана', description: 'Сводные показатели для списка чисел.' },
    { routeKey: 'fraction-calculator', label: 'Калькулятор дробей', description: 'Точные действия с обыкновенными дробями.' },
  ],
} as const satisfies UtilityRouteDefinition<'unit-converter', string>

export const unitConverterPairRoutes = unitConverterPairs.map((pair) => ({
  routeKey: `unit-converter-${pair.pairKey}` as const,
  path: pair.path,
  title: pair.title,
  description: `Переведите ${pair.shortTitle} онлайн. Формула: ${pair.formula}. Расчёт выполняется мгновенно и локально в браузере.`,
  nav: { label: pair.shortTitle, category: 'converter', order: 100, hidden: true },
  seo: {
    title: `${pair.title} онлайн — конвертер calcdeck.ru`,
    description: `${pair.title}: точный бесплатный перевод единиц по формуле «${pair.formula}». Пример: ${pair.example}. Без регистрации.`,
    changefreq: 'monthly' as const,
    priority: 0.9,
  },
  processing,
  analyticsKey: `unit-converter-${pair.pairKey}`,
  availability: 'available' as const,
  schema: {
    applicationCategory: 'UtilitiesApplication',
    softwareName: `${pair.title} — конвертер`,
    howTo: [
      `Оставьте выбранными единицы «${pair.shortTitle}» или верните их кнопкой сброса.`,
      `Введите исходное значение. Используемая формула: ${pair.formula}.`,
      `Получите результат автоматически. Например: ${pair.example}.`,
    ],
    faq: [
      { question: `Как перевести ${pair.shortTitle}?`, answer: `Используйте формулу: ${pair.formula}. Введите исходное число — конвертер применит её автоматически.` },
      { question: `Приведите пример перевода ${pair.shortTitle}`, answer: `Пример расчёта: ${pair.example}.` },
      { question: 'Расчёт выполняется локально?', answer: 'Да. Значение обрабатывается только в браузере и не отправляется на сервер.' },
    ],
  },
  relatedTools: [
    { routeKey: 'unit-converter', label: 'Все единицы измерения', description: 'Шесть категорий бытовых единиц в одном конвертере.' },
    { routeKey: `unit-converter-${pair.reversePairKey}`, label: unitConverterPairs.find((item) => item.pairKey === pair.reversePairKey)!.title, description: 'Обратное направление перевода единиц.' },
  ],
})) satisfies UtilityRouteDefinition<UnitConverterRouteKey, string>[]

export function pairByRouteKey(routeKey: string): UnitConverterPair | null {
  if (!routeKey.startsWith('unit-converter-')) return null
  const pairKey = routeKey.slice('unit-converter-'.length)
  return unitConverterPairs.find((pair) => pair.pairKey === pairKey) ?? null
}
